import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Card from 'components/card';
import MiniStatistics from 'components/card/MiniStatistics';
import SearchIcon from 'components/icons/SearchIcon';
import { MdOutlineShoppingCart, MdTrendingDown, MdWarning, MdAdd, MdInventory2 } from 'react-icons/md';
import { FaArrowRotateRight } from 'react-icons/fa6';
import { getPurchases, createPurchase } from 'interfaces/purchases';
import { getStock } from 'interfaces/stock';
import { getPaginatedOrders } from 'interfaces/order';
import { getSuppliers } from 'interfaces/supplier';
import { toast } from 'react-toastify';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface OrderLine {
  orderId: string;
  orderRef: string;
  status: 'pending' | 'draft';
  deliveryDate: string | null;
  customerName: string;
  qty: number;
  unit: string;
  unitPrice: number;
}

interface ForecastRow {
  productId: string;
  productName: string;
  unit: string;
  supplierId: string | null;
  supplierName: string;
  // quantities
  stockQty: number;
  alertLevel: number;
  committedQty: number;     // reserved by confirmed pending client orders
  draftQty: number;         // reserved by draft client orders
  incomingQty: number;      // arriving via pending purchase orders
  availableQty: number;     // stockQty + incomingQty - committedQty - draftQty
  suggestedQty: number;
  // purchase info
  lastPurchasePrice: number | null;
  vat: number;              // taux de TVA d'achat (en %)
  // order detail lines
  orderLines: OrderLine[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const _numFmt = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const _moneyFmt = new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmt = (n: number, unit = '') =>
  `${_numFmt.format(n)}${unit ? '\u00a0' + unit : ''}`;

const fmtMoney = (n: number) => _moneyFmt.format(n);

const availabilityColor = (row: ForecastRow) => {
  if (row.availableQty <= 0) return 'text-red-600 font-bold';
  if (row.availableQty < row.alertLevel) return 'text-amber-500 font-semibold';
  return 'text-green-600';
};

const generatePurchaseId = (): string => {
  const ts = Date.now().toString(36).toUpperCase();
  const rand = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `PO-${ts}${rand}`.substring(0, 8);
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const PurchaseForecastPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<ForecastRow[]>([]);
  const [suppliers, setSuppliers] = useState<any[]>([]);
  const [selectedSupplier, setSelectedSupplier] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [hideOk, setHideOk] = useState(false);
  const [generating, setGenerating] = useState<string | null>(null);
  const [sortCol, setSortCol] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  const handleSort = useCallback((col: string) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  }, [sortCol]);

  // quantity overrides keyed by productId
  const [qtyOverrides, setQtyOverrides] = useState<Record<string, number>>({});
  // detail panel
  const [detailRow, setDetailRow] = useState<ForecastRow | null>(null);

  // ---------------------------------------------------------------------------
  // Data loading
  // ---------------------------------------------------------------------------

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [stockRes, purchasesRes, pendingOrdersRes, draftOrdersRes, suppliersRes] = await Promise.all([
        getStock(),
        (getPurchases().catch((): null => null) as Promise<any>),
        (getPaginatedOrders(1, 1000, 'pending', '', 'all', 'deliveryDate').catch((): null => null) as Promise<any>),
        (getPaginatedOrders(1, 1000, 'draft', '', 'all', 'deliveryDate').catch((): null => null) as Promise<any>),
        (getSuppliers().catch((): null => null) as Promise<any>),
      ]);

      // /stock returns a plain array directly in stockRes.data
      const stockItems: any[] = Array.isArray(stockRes?.data) ? stockRes.data : (stockRes?.data?.docs || stockRes?.data?.data || []);
      const purchases: any[] = purchasesRes?.docs || purchasesRes?.results || purchasesRes?.data || purchasesRes || [];

      const pendingDocs: any[] = pendingOrdersRes?.docs || pendingOrdersRes?.results || pendingOrdersRes?.data || [];
      const draftDocs: any[]   = draftOrdersRes?.docs  || draftOrdersRes?.results  || draftOrdersRes?.data  || [];
      
      const seenOrderIds = new Set<string>();
      const orders: any[] = [];
      for (const o of [...pendingDocs, ...draftDocs]) {
        const id = String(o._id || o.id || '');
        if (id && seenOrderIds.has(id)) continue;
        if (id) seenOrderIds.add(id);
        orders.push(o);
      }
      const supplierList: any[] = suppliersRes?.docs || suppliersRes?.data || suppliersRes || [];
      setSuppliers(supplierList);

      // Supplier lookup map
      const supplierMap = new Map<string, any>(
        supplierList.map((s: any) => [String(s._id || s.id), s])
      );

      // -----------------------------------------------------------------------
      // Build product map from stock
      // -----------------------------------------------------------------------

      const productMap = new Map<string, ForecastRow>();

      for (const item of stockItems) {
        // /stock returns the product object directly (not nested under productId)
        const pid = String(item._id || '');
        if (!pid) continue;
        // Skip ingredients if needed — items with type 'ingredient' have no supplier concept
        // We include them anyway for completeness

        // Resolve supplier: products have a `suppliers` array of ObjectId refs
        // We try to match the first supplier in the array
        const firstSuppId = Array.isArray(item.suppliers) && item.suppliers.length > 0
          ? String(item.suppliers[0]?._id || item.suppliers[0] || '')
          : String(item.supplierId || '');
        const supp = supplierMap.get(firstSuppId);
        const supplierName = supp
          ? (supp.displayName || supp.company || supp.name || '—')
          : '—';

        productMap.set(pid, {
          productId: pid,
          productName: item.name || pid,
          unit: item.unity || '',          // field is `unity` in the schema
          supplierId: firstSuppId || null,
          supplierName,
          stockQty: Number(item.quantity ?? 0),
          alertLevel: Number(item.alert ?? 0), // field is `alert` in the schema
          committedQty: 0,
          draftQty: 0,
          incomingQty: 0,
          availableQty: 0,
          suggestedQty: 0,
          lastPurchasePrice: Number(item.purchasePrice ?? null) || null,
          vat: Number(item.vatPurchase ?? 0) || 0, // field is `vatPurchase` in the product schema
          orderLines: [],
        });
      }

      // -----------------------------------------------------------------------
      // Aggregate committed qty from pending & draft client orders
      // -----------------------------------------------------------------------

      for (const order of orders) {
        const isDraft = order.status === 'draft';
        const customerName =
          order.customer?.name || order.customer?.company || order.customerName ||
          order.customerId?.name || '—';
        const orderRef = order.orderId || order._id || '';
        const deliveryDate = order.deliveryDate ? new Date(order.deliveryDate).toLocaleDateString('fr-FR') : null;
        const items: any[] = Array.isArray(order.items) ? order.items : [];
        for (const it of items) {
          const pid = String(it.productId?._id || it.productId || '');
          // Skip items with no product ref or that are not in stock (custom line items)
          if (!pid || !productMap.has(pid)) continue;
          const qty = Number(it.quantity ?? it.qty ?? 0);
          const row = productMap.get(pid)!;
          if (isDraft) {
            row.draftQty += qty;
          } else {
            row.committedQty += qty;
          }
          row.orderLines.push({
            orderId: order._id || order.id || '',
            orderRef,
            status: isDraft ? 'draft' : 'pending',
            deliveryDate,
            customerName,
            qty,
            unit: it.unity || it.unit || row.unit || '',
            unitPrice: Number(it.price ?? 0),
          });
        }
      }

      // -----------------------------------------------------------------------
      // Aggregate incoming qty from pending purchase orders
      // -----------------------------------------------------------------------

      const pendingPurchases = purchases.filter((p: any) =>
        p.status === 'pending' || p.status === 'partial'
      );

      for (const po of pendingPurchases) {
        const items: any[] = Array.isArray(po.items) ? po.items : [];
        for (const it of items) {
          const pid = String(it.productId?._id || it.productId || '');
          if (!pid) continue;
          const ordered = Number(it.quantity ?? it.qty ?? 0);
          const received = Number(it.receivedQty ?? it.received ?? 0);
          const remaining = Math.max(0, ordered - received);
          if (productMap.has(pid)) {
            productMap.get(pid)!.incomingQty += remaining;
            // update last purchase price if available
            const price = Number(it.price ?? it.unitPrice ?? 0);
            if (price > 0) {
              productMap.get(pid)!.lastPurchasePrice = price;
            }
            // try to resolve supplier from PO if not set
            if (!productMap.get(pid)!.supplierId || productMap.get(pid)!.supplierId === 'null') {
              const poSuppId = String(po.supplier?._id || po.supplier || po.supplierId || '');
              if (poSuppId) {
                const supp = supplierMap.get(poSuppId);
                productMap.get(pid)!.supplierId = poSuppId;
                productMap.get(pid)!.supplierName = supp
                  ? (supp.displayName || supp.company || supp.name)
                  : productMap.get(pid)!.supplierName;
              }
            }
          }
        }
      }

      // -----------------------------------------------------------------------
      // Compute available & suggested
      // -----------------------------------------------------------------------

      for (const row of productMap.values()) {
        // disponible = stock + entrées attendues - commandes confirmées - brouillons
        row.availableQty = row.stockQty + row.incomingQty - row.committedQty - row.draftQty;
        // déficit : on couvre le seuil d'alerte ET toutes les commandes (confirmées + brouillons)
        const totalDemand = row.committedQty + row.draftQty;
        const deficit = Math.max(row.alertLevel, totalDemand) - (row.stockQty + row.incomingQty);
        row.suggestedQty = Math.max(0, Math.ceil(deficit));
      }

      setRows([...productMap.values()].sort((a, b) => a.availableQty - b.availableQty));
    } catch (err) {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ---------------------------------------------------------------------------
  // Filtered rows
  // ---------------------------------------------------------------------------

  const filteredRows = useMemo(() => {
    let result = rows;
    if (hideOk) result = result.filter(r => r.availableQty < r.alertLevel || r.suggestedQty > 0);
    if (selectedSupplier !== 'all') result = result.filter(r => r.supplierId === selectedSupplier);
    if (search.trim()) {
      const s = search.toLowerCase();
      result = result.filter(r =>
        r.productName.toLowerCase().includes(s) ||
        r.supplierName.toLowerCase().includes(s)
      );
    }
    if (sortCol) {
      result = [...result].sort((a, b) => {
        let va: any;
        let vb: any;
        switch (sortCol) {
          case 'productName': va = a.productName.toLowerCase(); vb = b.productName.toLowerCase(); break;
          case 'supplierName': va = a.supplierName.toLowerCase(); vb = b.supplierName.toLowerCase(); break;
          case 'stockQty': va = a.stockQty; vb = b.stockQty; break;
          case 'committedQty': va = a.committedQty; vb = b.committedQty; break;
          case 'draftQty': va = a.draftQty; vb = b.draftQty; break;
          case 'incomingQty': va = a.incomingQty; vb = b.incomingQty; break;
          case 'availableQty': va = a.availableQty; vb = b.availableQty; break;
          case 'alertLevel': va = a.alertLevel; vb = b.alertLevel; break;
          case 'suggestedQty': va = a.suggestedQty; vb = b.suggestedQty; break;
          default: return 0;
        }
        if (va < vb) return sortDir === 'asc' ? -1 : 1;
        if (va > vb) return sortDir === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return result;
  }, [rows, hideOk, selectedSupplier, search, sortCol, sortDir]);

  // ---------------------------------------------------------------------------
  // KPIs
  // ---------------------------------------------------------------------------

  const kpis = useMemo(() => {
    const criticalCount = rows.filter(r => r.availableQty <= 0).length;
    const warningCount = rows.filter(r => r.availableQty > 0 && r.availableQty < r.alertLevel).length;
    const totalCommitted = rows.reduce((s, r) => s + r.committedQty, 0);
    const totalDraft = rows.reduce((s, r) => s + r.draftQty, 0);
    const totalIncoming = rows.reduce((s, r) => s + r.incomingQty, 0);
    return { criticalCount, warningCount, totalCommitted, totalDraft, totalIncoming };
  }, [rows]);

  // ---------------------------------------------------------------------------
  // Grouped rows for "generate PO" by supplier
  // ---------------------------------------------------------------------------

  const rowsNeedingOrder = useMemo(() => {
    return filteredRows.filter(r => {
      const qty = qtyOverrides[r.productId] !== undefined ? qtyOverrides[r.productId] : r.suggestedQty;
      return qty > 0;
    });
  }, [filteredRows, qtyOverrides]);

  const supplierGroups = useMemo(() => {
    const groups: Record<string, { supplierId: string; supplierName: string; rows: ForecastRow[] }> = {};
    for (const r of rowsNeedingOrder) {
      const key = r.supplierId || '__unknown__';
      if (!groups[key]) {
        groups[key] = { supplierId: r.supplierId || '', supplierName: r.supplierName, rows: [] };
      }
      groups[key].rows.push(r);
    }
    return Object.values(groups);
  }, [rowsNeedingOrder]);

  // ---------------------------------------------------------------------------
  // Generate purchase order
  // ---------------------------------------------------------------------------

  const handleGeneratePO = useCallback(async (group: { supplierId: string; supplierName: string; rows: ForecastRow[] }) => {
    if (generating !== null) return; // protection double-submit
    setGenerating(group.supplierId || '__unknown__');
    try {
      const items = group.rows.map(r => {
        const quantity = qtyOverrides[r.productId] !== undefined ? qtyOverrides[r.productId] : r.suggestedQty;
        const price = r.lastPurchasePrice ?? 0;
        return {
          productId: r.productId,
          quantity,
          price,                       // unit price ht
          vat: Number(r.vat) || 0,     // purchase VAT rate (%) — the backend expects `vat` per line
          name: r.productName,
          unity: r.unit,               // the backend schema expects `unity` (not `unit`)
        };
      });
      const totalHT = items.reduce((s, it) => s + it.quantity * (it.price || 0), 0);
      const totalTVA = items.reduce((s, it) => s + it.quantity * (it.price || 0) * ((Number(it.vat) || 0) / 100), 0);
      const totalTTC = totalHT + totalTVA;
      const payload: any = {
        supplierId: group.supplierId || '',
        supplier: group.supplierId || '',
        purchaseId: generatePurchaseId(),
        items,
        totalHT: Number(totalHT.toFixed(2)),
        totalPrice: Number(totalTTC.toFixed(2)), // totalPrice = TTC
        status: 'pending',
        purchaseDate: new Date().toISOString(),
        deliveryFees: 0,
      };
      const created = await createPurchase(payload);
      if (created) {
        toast.success(`Bon de commande créé pour ${group.supplierName}`);
        loadData();
      } else {
        toast.error('Erreur lors de la création du bon de commande');
      }
    } finally {
      setGenerating(null);
    }
  }, [generating, qtyOverrides, loadData]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <div className="mt-4 grid h-full w-full grid-cols-1 gap-5 xl:mt-3">

      {/* ── KPI row ── */}
      <div className="z-0 col-span-12 grid h-full w-full grid-cols-2 gap-3 sm:gap-5 md:grid-cols-3 xl:grid-cols-5">
        <MiniStatistics
          name="Ruptures de stock"
          value={kpis.criticalCount}
          icon={<MdWarning color="#ef4444" />}
          iconBg="bg-red-50"
        />
        <MiniStatistics
          name="Stock faible"
          value={kpis.warningCount}
          icon={<MdTrendingDown color="#f59e0b" />}
          iconBg="bg-amber-50"
        />
        <MiniStatistics
          name="Commandes (confirmés)"
          value={fmt(kpis.totalCommitted)}
          icon={<MdOutlineShoppingCart color="#0260CB" />}
          iconBg="bg-lightPrimary"
        />
        <MiniStatistics
          name="Brouillons"
          value={fmt(kpis.totalDraft)}
          icon={<MdOutlineShoppingCart color="#7928ca" />}
          iconBg="bg-purple-50"
        />
        <MiniStatistics
          name="En cours de livraison"
          value={fmt(kpis.totalIncoming)}
          icon={<MdInventory2 color="#17ad37" />}
          iconBg="bg-green-50"
        />
      </div>

      {/* ── Main card ── */}
      <div className="col-span-12 h-full w-full rounded-[20px]">
        <Card extra="pt-[10px] pl-[20px] pr-[20px] pb-[20px] border overflow-visible">

          {/* ── Tabs-style header ── */}
          <div className="w-full flex items-center justify-between mb-4 border-b border-gray-200">
            <div className="flex gap-[2px] sm:gap-1">
              <div className="border-b-[4px] border-[#0260CB] pb-[6px] pt-[6px] bg-[#7090B0]/[0.05] flex items-center justify-center rounded-t-[15px] w-[210px] h-[40px] px-3">
                <p className="text-xs font-medium text-navy-700 dark:text-white lg:text-[15px] whitespace-nowrap">
                  Prévision des achats
                </p>
              </div>
            </div>
            <button
              onClick={loadData}
              disabled={loading}
              className="flex items-center gap-1.5 text-xs text-gray-500 border border-gray-200 rounded-xl px-3 py-1.5 hover:bg-lightPrimary transition-colors disabled:opacity-50 mb-1"
            >
              <FaArrowRotateRight className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </button>
          </div>

          {/* ── Filters row ── */}
          <div className="mt-2 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-3 mb-5">
            <div className="flex w-full gap-3 flex-wrap">
              <div className="flex h-[38px] items-center rounded-xl bg-lightPrimary text-sm text-gray-600 flex-1 min-w-[200px]">
                <SearchIcon />
                <input
                  type="text"
                  placeholder="Rechercher un produit ou fournisseur…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="block w-full rounded-full bg-lightPrimary text-sm text-navy-700 outline-none dark:!bg-navy-900 dark:text-white"
                />
              </div>
              <select
                value={selectedSupplier}
                onChange={e => setSelectedSupplier(e.target.value)}
                className="rounded-xl border border-gray-300 px-3 py-2 text-sm bg-white outline-none"
              >
                <option value="all">Tous les fournisseurs</option>
                {suppliers.map((s: any) => {
                  const id = String(s._id || s.id);
                  const name = s.displayName || s.company || s.name || '(sans nom)';
                  return <option key={id} value={id}>{name}</option>;
                })}
              </select>
              <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer select-none h-[38px] px-3 rounded-xl border border-gray-300 bg-white">
                <input
                  type="checkbox"
                  checked={hideOk}
                  onChange={e => setHideOk(e.target.checked)}
                  className="rounded accent-brand-500"
                />
                Alertes uniquement
              </label>
            </div>
          </div>

          {/* ── Table ── */}
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500" />
            </div>
          ) : (
            <>
              {/* ── Desktop table ── */}
              <div className="hidden md:block overflow-x-auto rounded-[20px] border border-gray-100">
                <table className="w-full text-xs text-left text-gray-700 border-collapse">
                  <thead className="text-[10px] text-gray-500 uppercase bg-gray-50 border-b border-gray-100">
                    <tr>
                      {([
                        { key: 'productName', label: 'Produit', align: 'left' },
                        { key: 'supplierName', label: 'Fournisseur', align: 'left' },
                        { key: 'stockQty', label: 'Stock actuel', align: 'right' },
                        { key: 'committedQty', label: 'Commandé', align: 'right' },
                        { key: 'draftQty', label: 'Brouillon', align: 'right' },
                        { key: 'incomingQty', label: 'En commande', align: 'right' },
                        { key: 'availableQty', label: 'Disponible net', align: 'right' },
                        { key: 'alertLevel', label: 'Seuil alerte', align: 'right' },
                        { key: 'suggestedQty', label: 'Suggéré', align: 'right' },
                        { key: null, label: 'À commander', align: 'right' },
                      ] as { key: string | null; label: string; align: string }[]).map(col => (
                        <th
                          key={col.key ?? '__order__'}
                          onClick={() => col.key && handleSort(col.key)}
                          className={`px-3 py-3 whitespace-nowrap select-none ${col.align === 'right' ? 'text-right' : ''} ${col.key ? 'cursor-pointer hover:text-navy-700 hover:bg-gray-100 transition-colors' : ''}`}
                        >
                          <span className="inline-flex items-center gap-1 justify-end">
                            {col.label}
                            {col.key && (
                              <span className="text-[9px]">
                                {sortCol === col.key ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
                              </span>
                            )}
                          </span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredRows.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="text-center py-12 text-gray-400 text-sm">
                          Aucun produit trouvé
                        </td>
                      </tr>
                    ) : filteredRows.map(row => {
                      const overrideQty = qtyOverrides[row.productId];
                      const displayQty = overrideQty !== undefined ? overrideQty : row.suggestedQty;
                      const isRupture = row.availableQty <= 0;
                      const isFaible = !isRupture && row.alertLevel > 0 && row.availableQty < row.alertLevel;
                      return (
                        <tr
                          key={row.productId}
                          onClick={() => row.orderLines.length > 0 && setDetailRow(row)}
                          className={`transition-colors
                            ${isRupture ? 'bg-red-50/40' : isFaible ? 'bg-amber-50/30' : 'bg-white'}
                            ${row.orderLines.length > 0 ? 'hover:bg-[#7090B0]/[0.05] cursor-pointer' : 'hover:bg-gray-50/60'}
                          `}
                        >
                          <td className="px-3 py-2.5">
                            <span className="font-medium text-navy-700 dark:text-white">{row.productName}</span>
                            {row.unit && <span className="ml-1 text-[10px] text-gray-400">({row.unit})</span>}
                            {row.orderLines.length > 0 && (
                              <span className="ml-2 text-[10px] text-[#0260CB] underline underline-offset-2">
                                {row.orderLines.length} commande{row.orderLines.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-gray-600 whitespace-nowrap">{row.supplierName}</td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap font-semibold text-navy-700">
                            {fmt(row.stockQty)}
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap font-medium text-blue-600">
                            {row.committedQty > 0 ? `- ${fmt(row.committedQty)}` : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap font-medium text-purple-500">
                            {row.draftQty > 0
                              ? <span title="Commandes en brouillon">~ {fmt(row.draftQty)}</span>
                              : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap font-medium text-green-600">
                            {row.incomingQty > 0 ? `+ ${fmt(row.incomingQty)}` : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap">
                            <span className={`font-bold ${availabilityColor(row)}`}>{fmt(row.availableQty)}</span>
                            {isRupture && (
                              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-red-100 text-red-700">Rupture</span>
                            )}
                            {isFaible && (
                              <span className="ml-1.5 inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-amber-100 text-amber-700">Faible</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap text-gray-400">
                            {row.alertLevel > 0 ? fmt(row.alertLevel) : <span className="text-gray-300">—</span>}
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap">
                            {row.suggestedQty > 0 ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 text-orange-700">
                                {fmt(row.suggestedQty)}
                              </span>
                            ) : (
                              <span className="text-gray-300">—</span>
                            )}
                          </td>
                          <td className="px-3 py-2.5 text-right whitespace-nowrap" onClick={e => e.stopPropagation()}>
                            <input
                              type="number"
                              min={0}
                              value={displayQty}
                              onChange={e => {
                                const val = Math.max(0, Number(e.target.value));
                                setQtyOverrides(prev => ({ ...prev, [row.productId]: val }));
                              }}
                              className="w-20 rounded-xl border border-gray-200 px-2 py-1 text-xs text-right outline-none focus:border-[#0260CB] bg-lightPrimary"
                            />
                            {row.lastPurchasePrice !== null && displayQty > 0 && (
                              <div className="text-[10px] text-gray-400 mt-0.5 text-right">
                                ≈ {fmtMoney(displayQty * row.lastPurchasePrice)}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* ── Mobile cards  ── */}
              <div className="md:hidden space-y-3">
                {filteredRows.length === 0 ? (
                  <div className="text-center py-10 text-gray-400 text-sm">Aucun produit trouvé</div>
                ) : filteredRows.map(row => {
                  const overrideQty = qtyOverrides[row.productId];
                  const displayQty = overrideQty !== undefined ? overrideQty : row.suggestedQty;
                  const isRupture = row.availableQty <= 0;
                  const isFaible = !isRupture && row.alertLevel > 0 && row.availableQty < row.alertLevel;
                  return (
                    <div
                      key={row.productId}
                      className={`rounded-xl border p-4 shadow-sm
                        ${isRupture ? 'bg-red-50/60 border-red-100' : isFaible ? 'bg-amber-50/40 border-amber-100' : 'bg-white border-gray-100'}
                        ${row.orderLines.length > 0 ? 'cursor-pointer active:bg-[#7090B0]/[0.05]' : ''}
                      `}
                      onClick={() => row.orderLines.length > 0 && setDetailRow(row)}
                    >
                      {/* Header */}
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div className="min-w-0">
                          <div className="font-semibold text-navy-700 text-sm truncate">
                            {row.productName}
                            {row.unit && <span className="ml-1 text-[10px] font-normal text-gray-400">({row.unit})</span>}
                          </div>
                          <div className="text-xs text-gray-400 mt-0.5">{row.supplierName || '—'}</div>
                        </div>
                        <div className="flex flex-col items-end gap-1 shrink-0">
                          {isRupture && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-red-100 text-red-700">Rupture</span>}
                          {isFaible && <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-amber-100 text-amber-700">Faible</span>}
                          {row.orderLines.length > 0 && (
                            <span className="text-[10px] text-[#0260CB] underline underline-offset-2">
                              {row.orderLines.length} commande{row.orderLines.length > 1 ? 's' : ''}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Quantities grid */}
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs mb-3">
                        <div className="flex justify-between">
                          <span className="text-gray-400">Stock actuel</span>
                          <span className="font-semibold text-navy-700">{fmt(row.stockQty)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-400">Disponible net</span>
                          <span className={`font-bold ${availabilityColor(row)}`}>{fmt(row.availableQty)}</span>
                        </div>
                        {row.committedQty > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">Engagé</span>
                            <span className="font-medium text-blue-600">- {fmt(row.committedQty)}</span>
                          </div>
                        )}
                        {row.draftQty > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">Brouillon</span>
                            <span className="font-medium text-purple-500">~ {fmt(row.draftQty)}</span>
                          </div>
                        )}
                        {row.incomingQty > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">En commande</span>
                            <span className="font-medium text-green-600">+ {fmt(row.incomingQty)}</span>
                          </div>
                        )}
                        {row.alertLevel > 0 && (
                          <div className="flex justify-between">
                            <span className="text-gray-400">Seuil alerte</span>
                            <span className="text-gray-500">{fmt(row.alertLevel)}</span>
                          </div>
                        )}
                        {row.suggestedQty > 0 && (
                          <div className="flex justify-between col-span-2">
                            <span className="text-gray-400">Suggéré</span>
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-100 text-orange-700">{fmt(row.suggestedQty)}</span>
                          </div>
                        )}
                      </div>

                      {/* Ordered */}
                      <div className="flex items-center justify-between border-t border-gray-100 pt-3" onClick={e => e.stopPropagation()}>
                        <span className="text-xs text-gray-500">À commander</span>
                        <div className="flex flex-col items-end gap-0.5">
                          <input
                            type="number"
                            min={0}
                            value={displayQty}
                            onChange={e => {
                              const val = Math.max(0, Number(e.target.value));
                              setQtyOverrides(prev => ({ ...prev, [row.productId]: val }));
                            }}
                            className="w-24 rounded-xl border border-gray-200 px-3 py-1.5 text-xs text-right outline-none focus:border-[#0260CB] bg-lightPrimary"
                          />
                          {row.lastPurchasePrice !== null && displayQty > 0 && (
                            <div className="text-[10px] text-gray-400">≈ {fmtMoney(displayQty * row.lastPurchasePrice)}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* ── resume result ── */}
              <div className="mt-3 text-xs text-gray-400">{filteredRows.length} produit{filteredRows.length > 1 ? 's' : ''} affiché{filteredRows.length > 1 ? 's' : ''}</div>

              {/* ── Suggested purchase orders section ── */}
              {supplierGroups.length > 0 && (
                <div className="mt-8">
                  <div className="mb-3 flex items-center gap-2">
                    <p className="text-sm font-bold text-navy-700 dark:text-white">Bons de commande suggérés</p>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-brand-50 text-brand-500">
                      {supplierGroups.length} ordre d'achat{supplierGroups.length > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                    {supplierGroups.map(group => {
                      const total = group.rows.reduce((s, r) => {
                        const qty = qtyOverrides[r.productId] !== undefined ? qtyOverrides[r.productId] : r.suggestedQty;
                        return s + qty * (r.lastPurchasePrice || 0);
                      }, 0);
                      const isGenerating = generating === (group.supplierId || '__unknown__');
                      const noSupplier = !group.supplierId;
                      return (
                        <div
                          key={group.supplierId || '__unknown__'}
                          className="rounded-[20px] border border-gray-100 p-4 bg-white shadow-3xl shadow-shadow-500 flex flex-col"
                        >
                          {/* Card header */}
                          <div className="flex items-start justify-between mb-3 gap-2">
                            <div className="min-w-0">
                              <p className="text-sm font-bold text-toola-700 dark:text-white truncate">{group.supplierName}</p>
                              <p className="text-[11px] text-gray-400 mt-0.5">{group.rows.length} article{group.rows.length > 1 ? 's' : ''}</p>
                            </div>
                            {total > 0 && (
                              <span className="text-sm font-bold text-toola-700 shrink-0">{fmtMoney(total)}</span>
                            )}
                          </div>

                          {/* Articles list */}
                          <ul className="space-y-2 mb-4 border-t border-gray-50 pt-3 flex-1">
                            {group.rows.map(r => {
                              const qty = qtyOverrides[r.productId] !== undefined ? qtyOverrides[r.productId] : r.suggestedQty;
                              return (
                                <li key={r.productId} className="flex flex-col xs:flex-row xs:items-baseline xs:justify-between gap-0.5 xs:gap-2 text-xs">
                                  <span className="truncate text-gray-600 max-w-full xs:max-w-[55%]">{r.productName}</span>
                                  <span className="font-semibold text-navy-700 whitespace-nowrap">
                                    {fmt(qty)}{r.unit ? `\u00a0${r.unit}` : ''}
                                    {r.lastPurchasePrice
                                      ? <span className="text-gray-400 font-normal"> · {fmtMoney(qty * r.lastPurchasePrice)}</span>
                                      : ''}
                                  </span>
                                </li>
                              );
                            })}
                          </ul>

                          {/* Footer action */}
                          {noSupplier ? (
                            <div className="w-full flex items-start gap-2 text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-xl py-2.5 px-3">
                              <MdWarning className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                              <span>Aucun fournisseur associé — assignez un fournisseur aux produits pour créer un bon de commande.</span>
                            </div>
                          ) : (
                            <button
                              onClick={() => handleGeneratePO(group)}
                              disabled={isGenerating}
                              className="w-full flex items-center justify-center gap-1.5 text-xs font-medium bg-toola-500 text-white rounded-xl py-2.5 hover:bg-toola-600 disabled:opacity-50 transition-colors active:scale-[0.98]"
                            >
                              {isGenerating ? (
                                <span className="animate-spin rounded-full h-3 w-3 border-b border-white" />
                              ) : (
                                <MdAdd className="w-3.5 h-3.5" />
                              )}
                              Créer le bon de commande
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {filteredRows.length > 0 && rowsNeedingOrder.length === 0 && (
                <div className="mt-6 flex items-center justify-center gap-2 text-sm text-green-600 py-4 rounded-[20px] bg-green-50 border border-green-100">
                  <MdInventory2 className="w-4 h-4" />
                  Tous les produits ont un stock suffisant — aucune commande suggérée.
                </div>
              )}
            </>
          )}
        </Card>
      </div>

      {/* ── Detail drawer ── */}
      {detailRow && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-black/20" onClick={() => setDetailRow(null)} />
          <div className="relative z-10 w-full max-w-lg bg-white h-full flex flex-col shadow-2xl overflow-hidden">

            {/* Drawer header */}
            <div className="flex items-start justify-between px-6 py-5 border-b border-gray-100">
              <div>
                <h3 className="text-sm font-bold text-navy-700 dark:text-white">{detailRow.productName}</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {detailRow.orderLines.length} commande{detailRow.orderLines.length > 1 ? 's' : ''}&nbsp;·&nbsp;
                  stock : <strong className="text-navy-700">{fmt(detailRow.stockQty)}{detailRow.unit ? `\u00a0${detailRow.unit}` : ''}</strong>
                </p>
              </div>
              <button
                onClick={() => setDetailRow(null)}
                className="text-gray-400 hover:text-gray-600 text-lg leading-none mt-0.5"
              >
                ✕
              </button>
            </div>

            {/* Summary strip */}
            <div className="grid grid-cols-3 gap-px bg-gray-100">
              <div className="bg-white px-4 py-3 text-center">
                <p className="text-[10px] text-gray-400 mb-1">Confirmé</p>
                <p className="text-sm font-bold text-blue-600">{fmt(detailRow.committedQty)}{detailRow.unit ? `\u00a0${detailRow.unit}` : ''}</p>
              </div>
              <div className="bg-white px-4 py-3 text-center">
                <p className="text-[10px] text-gray-400 mb-1">Brouillon</p>
                <p className="text-sm font-bold text-purple-500">{fmt(detailRow.draftQty)}{detailRow.unit ? `\u00a0${detailRow.unit}` : ''}</p>
              </div>
              <div className="bg-white px-4 py-3 text-center">
                <p className="text-[10px] text-gray-400 mb-1">Disponible net</p>
                <p className={`text-sm font-bold ${availabilityColor(detailRow)}`}>{fmt(detailRow.availableQty)}{detailRow.unit ? `\u00a0${detailRow.unit}` : ''}</p>
              </div>
            </div>

            {/* Order lines */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">

              {/* Pending */}
              {detailRow.orderLines.filter(l => l.status === 'pending').length > 0 && (
                <div>
                  <p className="text-[10px] uppercase font-semibold text-gray-400 mb-2 tracking-wider">
                    Commandes confirmées&nbsp;({detailRow.orderLines.filter(l => l.status === 'pending').length})
                  </p>
                  <div className="space-y-2">
                    {detailRow.orderLines.filter(l => l.status === 'pending').map((line, i) => (
                      <div key={i} className="rounded-[15px] border border-gray-100 bg-white shadow-3xl shadow-shadow-500 px-4 py-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="inline-flex shrink-0 items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-[#7090B0]/[0.15] text-navy-700">
                              En cours
                            </span>
                            <span className="text-xs font-semibold text-navy-700 truncate">{line.customerName}</span>
                          </div>
                          <span className="text-xs font-bold text-[#0260CB] whitespace-nowrap">{fmt(line.qty)}{line.unit ? `\u00a0${line.unit}` : ''}</span>
                        </div>
                        <div className="flex items-center justify-between mt-2 text-[11px] text-gray-500">
                          <span className="font-mono text-gray-300">{line.orderRef}</span>
                          <div className="flex items-center gap-3">
                            {line.unitPrice > 0 && (
                              <span>
                                {fmtMoney(line.unitPrice)}/{line.unit || 'u.'}&nbsp;·&nbsp;
                                <strong className="text-navy-700">{fmtMoney(line.qty * line.unitPrice)}</strong>
                              </span>
                            )}
                            {line.deliveryDate && <span className="text-gray-400">📅 {line.deliveryDate}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Drafts */}
              {detailRow.orderLines.filter(l => l.status === 'draft').length > 0 && (
                <div>
                  <p className="text-[10px] uppercase font-semibold text-gray-400 mb-2 tracking-wider">
                    Brouillons&nbsp;({detailRow.orderLines.filter(l => l.status === 'draft').length})
                  </p>
                  <div className="space-y-2">
                    {detailRow.orderLines.filter(l => l.status === 'draft').map((line, i) => (
                      <div key={i} className="rounded-[15px] border border-purple-100 bg-white shadow-3xl shadow-shadow-500 px-4 py-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="inline-flex shrink-0 items-center px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-purple-100 text-purple-700">
                              Brouillon
                            </span>
                            <span className="text-xs font-semibold text-navy-700 truncate">{line.customerName}</span>
                          </div>
                          <span className="text-xs font-bold text-purple-600 whitespace-nowrap">{fmt(line.qty)}{line.unit ? `\u00a0${line.unit}` : ''}</span>
                        </div>
                        <div className="flex items-center justify-between mt-2 text-[11px] text-gray-500">
                          <span className="font-mono text-gray-300">{line.orderRef}</span>
                          <div className="flex items-center gap-3">
                            {line.unitPrice > 0 && (
                              <span>
                                {fmtMoney(line.unitPrice)}/{line.unit || 'u.'}&nbsp;·&nbsp;
                                <strong className="text-navy-700">{fmtMoney(line.qty * line.unitPrice)}</strong>
                              </span>
                            )}
                            {line.deliveryDate && <span className="text-gray-400">📅 {line.deliveryDate}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PurchaseForecastPage;

